import { getApiErrorMessage, IApiAxiosError } from "@/api";
import { checkSkillName, duplicateSkill, ISkill } from "@/api/skills";
import { OrganizationCombobox } from "@/components/shared/OrganizationCombobox";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router";

interface DuplicateSkillDialogProps {
    open: boolean;
    onClose: () => void;
    sourceSkill: ISkill;
}

export const DuplicateSkillDialog = ({
    open,
    onClose,
    sourceSkill,
}: DuplicateSkillDialogProps) => {
    const [name, setName] = React.useState(`${sourceSkill.name} (Copy)`);
    const [organizationId, setOrganizationId] = React.useState<number | null>(
        null
    );
    const [nameError, setNameError] = React.useState<string | null>(null);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // ── Debounced name uniqueness check ──────────────────────────────
    useEffect(() => {
        const trimmed = name.trim();
        if (!trimmed) return;

        const timer = setTimeout(async () => {
            try {
                const { available } = await checkSkillName(trimmed);
                if (!available) {
                    setNameError("A skill with this name already exists");
                }
            } catch {
                // Silently ignore — submit-time validation will catch it
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [name]);

    const duplicateMutation = useMutation({
        mutationFn: () =>
            duplicateSkill(sourceSkill.id, {
                name: name.trim(),
                organization_id: organizationId,
            }),
        onSuccess: (newSkill) => {
            queryClient.invalidateQueries({ queryKey: ["admin-skills"] });
            queryClient.invalidateQueries({ queryKey: ["admin-skills-all"] });
            navigate(`/admin/skills/${newSkill.id}/details`, {
                state: { justDuplicated: true },
            });
            handleClose();
        },
    });

    const handleClose = () => {
        onClose();
        setName(`${sourceSkill.name} (Copy)`);
        setOrganizationId(null);
        setNameError(null);
        duplicateMutation.reset();
    };

    const isValid = name.trim() !== "" && !nameError;

    const axiosError = duplicateMutation.error as IApiAxiosError | null;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Duplicate Skill</DialogTitle>
                    <DialogDescription>
                        Creates a new draft copy of this skill. The copy starts
                        unpublished.
                    </DialogDescription>
                </DialogHeader>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (isValid) duplicateMutation.mutate();
                    }}
                    className="space-y-4"
                >
                    <div className="space-y-1.5">
                        <Label
                            htmlFor="duplicate-skill-name"
                            className={
                                nameError ? "text-destructive" : undefined
                            }
                        >
                            Skill Name
                        </Label>
                        <Input
                            id="duplicate-skill-name"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                setNameError(null);
                            }}
                            autoFocus
                            aria-invalid={!!nameError}
                            disabled={duplicateMutation.isPending}
                        />
                        {nameError && (
                            <p className="text-destructive text-sm">
                                {nameError}
                            </p>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label>Organization</Label>
                        <OrganizationCombobox
                            value={organizationId}
                            onChange={setOrganizationId}
                            disabled={duplicateMutation.isPending}
                        />
                        <p className="text-muted-foreground text-sm">
                            Leave blank for global visibility across all
                            organizations
                        </p>
                    </div>

                    {axiosError && (
                        <p className="text-destructive text-sm">
                            {getApiErrorMessage(
                                axiosError,
                                "Something went wrong. Please try again."
                            )}
                        </p>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            type="button"
                            onClick={handleClose}
                            disabled={duplicateMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!isValid || duplicateMutation.isPending}
                        >
                            {duplicateMutation.isPending ? (
                                <>
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    Duplicating…
                                </>
                            ) : (
                                "Duplicate"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
